import { TransactionActionModals } from "@/modules/transaction/components";
import { useGetCurrencyExchangeTransferQuery } from "@/modules/transaction/services";
import { SideSheetPosition } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { ActionsMenu, EmptyOrErrorContent } from "@/shared/components";
import { getErrorMessage } from "@/shared/helpers";
import { useMediaQuery, useOperationActions } from "@/shared/hooks";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CurrencyExchangeDetailRows } from "./CurrencyExchangeDetailRows";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transactionId?: string;
}

export const CurrencyExchangeDetails = ({ isOpen, onClose, transactionId = "" }: Props) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery();

  const [isOpenActionsMenu, setIsOpenActionsMenu] = useState<boolean>(false);

  const {
    data: transfer,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetCurrencyExchangeTransferQuery(
    { id: transactionId },
    {
      skip: !transactionId,
      refetchOnMountOrArgChange: true,
    },
  );

  const toggleActionsMenu = () => {
    setIsOpenActionsMenu((prev) => !prev);
  };

  const {
    actions,
    isOpenSendToEdit,
    isDeleteModalOpen,
    setIsOpenSendToEdit,
    setIsDeleteModalOpen,
  } = useOperationActions({
    filters: transfer?.state?.transactionCommand,
    id: transactionId,
    type: transfer?.transactionType,
  });

  return (
    <>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isOpen}
        isBodyHidden={false}
        onClose={onClose}
        isWithDivider
        isLoading={isFetching}
        className="detail-side-sheet"
        size="small"
        topRightActions={
          !isFetching && !isError ? (
            <ActionsMenu
              isOpen={isOpenActionsMenu}
              actions={actions}
              buttonType="text"
              openMenu={toggleActionsMenu}
              closeMenu={toggleActionsMenu}
            />
          ) : null
        }
        title={t("transfer.currencyExchange.title")}
        shouldRemoveCallback={false}
        closeOnOutsideClick={false}
      >
        {({ scrollbarContainerRef }) => (
          <>
            {isFetching ? (
              <Spinner isFullScreen={false} size="small"/>
            ) : isError ? (
              <div className="full-width full-height flexbox align-items--center">
                <EmptyOrErrorContent
                  isError={true}
                  title={t(getErrorMessage(error as IError))}
                  action={refetch}
                />
              </div>
            ) : transfer && (
              <CurrencyExchangeDetailRows
                transfer={transfer}
                scrollbarContainerRef={scrollbarContainerRef}
              />
            )}
          </>
        )}
      </SideSheet>
      <TransactionActionModals
        onClose={onClose}
        isOpenSendToEdit={isOpenSendToEdit}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsOpenSendToEdit={setIsOpenSendToEdit}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
      />
    </>
  );
};
